import {
  VuexModule,
  Module,
  Action,
  Mutation,
  getModule,
} from "vuex-module-decorators";
import { login, logout, switchLogin, getUserInfo } from "@/api/staff";
import store from "@/store";
import { RouterModule } from "@/store/module/router";
import router from "@/router/index";
import { Message } from "element-ui";

export interface IUserState {
  userInfo: IUserInfo;
  token: string;
  loginDialogVisible: boolean;
  orgList: any[];
}

export interface IUserInfo {
  is_super: number;
  org_id: number;
  org_name: string;
  online_pay: boolean;
  inst_id: number;
  inst_org_number: number;
  staff_avatar_url: string;
  staff_id: number;
  staff_name: string;
  staff_mobile: string;
  integral_switch: number;
  order_course_switch: number;
  order_transfer_free_switch: number;
  org_teach_machine: number;
  course_package_switch: number;
  student_intention_claim_switch: number;
  order_edit_payment_time_max_days: number;
  roll_call_edit_edu_teacher_max_days: number;
}

@Module({ dynamic: true, namespaced: true, store, name: "user" })
class User extends VuexModule implements IUserState {
  userInfo: IUserInfo = {
    is_super: 0,
    org_id: 0,
    org_name: "",
    online_pay: false,
    inst_id: 0,
    inst_org_number: 0,
    staff_avatar_url: "",
    staff_id: 0,
    staff_name: "",
    staff_mobile: "",
    integral_switch: 2,
    order_course_switch: 2,
    order_transfer_free_switch: 2,
    org_teach_machine: 2,
    course_package_switch: 2,
    student_intention_claim_switch: 2,
    order_edit_payment_time_max_days: 0,
    roll_call_edit_edu_teacher_max_days: 0,
  };
  token = "";
  loginDialogVisible = false;
  orgList: any = [];

  /*
    ! getters
  */

  /*
    ! mutations
  */

  @Mutation
  public SET_USER_INFO(data: IUserInfo) {
    this.userInfo = data;
    localStorage.setItem("userInfo", JSON.stringify(data));
  }

  @Mutation
  public SET_USER_ORG_LIST(data: any[]) {
    this.orgList = data;
    localStorage.setItem("orgList", JSON.stringify(data));
  }

  @Mutation
  public SET_TOKEN(token: string) {
    this.token = token;
    localStorage.setItem("token", token);
  }

  @Mutation
  public LOGIN_OUT() {
    this.userInfo = {
      is_super: 0,
      org_id: 0,
      org_name: "",
      online_pay: false,
      inst_id: 0,
      inst_org_number: 0,
      staff_avatar_url: "",
      staff_id: 0,
      staff_name: "",
      staff_mobile: "",
      integral_switch: 2,
      order_course_switch: 2,
      order_transfer_free_switch: 2,
      org_teach_machine: 2,
      course_package_switch: 2,
      student_intention_claim_switch: 2,
      order_edit_payment_time_max_days: 0,
      roll_call_edit_edu_teacher_max_days: 0,
    };
    this.token = "";
    this.orgList = [];
    router.push({ name: "login", replace: true });
    sessionStorage.clear();
    localStorage.clear();
    window.location.reload();
  }

  @Mutation
  public SHOW_LOGIN_DIALOG() {
    this.loginDialogVisible = true;
  }

  @Mutation
  public HIDE_LOGIN_DIALOG() {
    this.loginDialogVisible = false;
  }

  /*
    ! actions
  */

  @Action({ rawError: true })
  public async LoginIn(loginInfo: any) {
    const res: any = await login(loginInfo);
    if (res.error_code == 0) {
      // 页面间通讯，通知重新登录
      const channel = new BroadcastChannel("mwow-page-channels");
      channel.postMessage("user-new-login");
      Message({
        message: "登录成功",
        type: "success",
      });
      this.SET_TOKEN(res.data.token);
      await this.updateUserInfo();
      this.HIDE_LOGIN_DIALOG();
      const _router: any = router;
      const redirect = _router.history.current.query.redirect;
      if (redirect) {
        router.push({ path: redirect });
      } else {
        router.push({ name: "dashboard" });
      }
    } else {
      Message({
        message: "登录失败：" + res.message,
        type: "error",
      });
    }
  }

  // 扫码登录
  @Action({ rawError: true })
  public async qrCodeLoginIn(token: string) {
    if (token) {
      // 页面间通讯，通知重新登录
      const channel = new BroadcastChannel("mwow-page-channels");
      channel.postMessage("user-new-login");
      Message({
        message: "登录成功",
        type: "success",
      });
      this.SET_TOKEN(token);
      await this.updateUserInfo();
      this.HIDE_LOGIN_DIALOG();
      const _router: any = router;
      const redirect = _router.history.current.query.redirect;
      if (redirect) {
        router.push({ path: redirect });
      } else {
        router.push({ name: "dashboard" });
      }
    }
  }

  @Action
  public async changeOrgLogin(org_id: number) {
    try {
      const res: any = await switchLogin({ org_id });
      if (res.error_code == 0) {
        Message({
          message: "切换成功",
          type: "success",
        });
        this.SET_TOKEN(res.data.token);
        await this.updateUserInfo();
        RouterModule.SetAsyncRouter();
        // 页面间通讯，通知切换门店
        const channel = new BroadcastChannel("mwow-page-channels");
        channel.postMessage("user-change-org");
        // 返回首页，避免query对不上
        window.location.href = window.location.origin;
      } else {
        Message({
          message: "切换失败：" + res.message,
          type: "error",
        });
      }
    } catch {
      Message({
        message: "切换失败",
        type: "error",
      });
    }
  }

  @Action
  public async LoginOut() {
    logout().then(() => {
      // 页面间通讯，通知退出登录
      const channel = new BroadcastChannel("mwow-page-channels");
      channel.postMessage("user-logout");
      this.LOGIN_OUT();
    });
  }

  @Action
  public async updateUserInfo() {
    const res: any = await getUserInfo();
    if (res.error_code == 0) {
      this.SET_USER_INFO({
        is_super: res.data.is_super,
        org_id: res.data.org.org_id,
        org_name: res.data.org.org_name,
        online_pay: res.data.org_config.online_pay,
        inst_id: res.data.org.inst_id,
        inst_org_number: res.data.inst_org_number,
        staff_avatar_url: res.data.avatar,
        staff_id: res.data.staff_id,
        staff_name: res.data.name,
        staff_mobile: res.data.mobile,
        integral_switch: res.data.org_config.integral_switch || 2,
        order_course_switch: res.data.org_config.order_course_switch || 2,
        order_transfer_free_switch:
          res.data.org_config.order_transfer_free_switch || 2,
        course_package_switch: res.data.org_config.course_package_switch || 2,
        student_intention_claim_switch:
          res.data.org_config.student_intention_claim_switch || 2,
        org_teach_machine: res.data.org.teach_machine || 2,
        order_edit_payment_time_max_days:
          res.data.org_config.order_edit_payment_time_max_days,
        roll_call_edit_edu_teacher_max_days:
          res.data.org_config.roll_call_edit_edu_teacher_max_days,
      });
      this.SET_USER_ORG_LIST(res.data.inst_list);
    }
  }
}

export const UserModule = getModule(User);
